import React from 'react';
import {
  Breadcrumbs,
  Link,
  Typography,
  Box,
} from '@mui/material';
import {
  Home as HomeIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';

const Breadcrumb = ({ sx = {} }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const getBreadcrumbItems = () => {
    const pathSegments = location.pathname.split('/').filter(segment => segment !== '');
    
    // Special handling for employee details page
    if (pathSegments[0] === 'employee' && pathSegments.length > 1) {
      return [
        {
          label: 'Dashboard',
          path: '/dashboard',
          icon: <HomeIcon sx={{ fontSize: 16 }} />,
        },
        {
          label: 'Employees',
          path: '/employees',
        },
        {
          label: 'Employee Details',
          path: '/employees',
        }
      ];
    }

    // Special handling for vendor details page
    if (pathSegments[0] === 'vendor' && pathSegments.length > 1) {
      return [
        {
          label: 'Dashboard',
          path: '/dashboard',
          icon: <HomeIcon sx={{ fontSize: 16 }} />,
        },
        {
          label: 'Vendors',
          path: '/vendors',
        },
        {
          label: 'Vendor Details',
          path: '/vendors',
        }
      ];
    }

    // Special handling for visitor details page
    if (pathSegments[0] === 'visitor' && pathSegments.length > 1) {
      return [
        {
          label: 'Dashboard',
          path: '/dashboard',
          icon: <HomeIcon sx={{ fontSize: 16 }} />,
        },
        {
          label: 'Visitors',
          path: '/visitors',
        },
        {
          label: 'Visitor Details',
          path: '/visitors',
        }
      ];
    }

    // Intrusion profile registration: /intrusion-profile/register-intrusion-profile
    if (
      pathSegments[0] === 'intrusion-profile' &&
      pathSegments[1] === 'register-intrusion-profile'
    ) {
      return [
        {
          label: 'Dashboard',
          path: '/dashboard',
          icon: <HomeIcon sx={{ fontSize: 16 }} />,
        },
        {
          label: 'Intrusion Profile',
          path: '/intrusion-profile',
        },
        {
          label: 'Register Intrusion Profile',
          path: '/intrusion-profile/register-intrusion-profile',
        },
      ];
    }

    // Vendor registration: /vendors/register-vendor
    if (pathSegments[0] === 'vendors' && pathSegments[1] === 'register-vendor') {
      return [
        {
          label: 'Dashboard',
          path: '/dashboard',
          icon: <HomeIcon sx={{ fontSize: 16 }} />,
        },
        {
          label: 'Vendors',
          path: '/vendors',
        },
        {
          label: 'Register Vendor',
          path: '/vendors/register-vendor',
        },
      ];
    }

    if (pathSegments[0] === 'visitors' && pathSegments[1] === 'register-visitor') {
      return [
        {
          label: 'Dashboard',
          path: '/dashboard',
          icon: <HomeIcon sx={{ fontSize: 16 }} />,
        },
        {
          label: 'Visitors',
          path: '/visitors',
        },
        {
          label: 'Register Visitor',
          path: '/visitors/register-visitor',
        },
      ];
    }
    
    // AI Matching: /ai-review (Settings submenu)
    if (pathSegments[0] === 'ai-review') {
      const crumbs = [
        {
          label: 'Dashboard',
          path: '/dashboard',
          icon: <HomeIcon sx={{ fontSize: 16 }} />,
        },
        {
          label: 'AI Matching',
          path: '/ai-review',
        },
      ];
      if (pathSegments[1] === 'statistics') {
        crumbs.push({
          label: 'Statistics',
          path: '/ai-review/statistics',
        });
      }
      return crumbs;
    }

    const breadcrumbMap = {
      'dashboard': 'Dashboard',
      'intrusion-dashboard': 'Intrusion Dashboard',
      'employees': 'Employees',
      'register': 'Register Employee',
      'vendors': 'Vendors',
      'visitors': 'Visitors',
      'register-vendor': 'Register Vendor',
      'register-visitor': 'Register Visitor',
      'intrusion-profile': 'Intrusion Profile',
      'intrusion-profiles': 'Intrusion Profile',
      'register-intrusion-profile': 'Register Intrusion Profile',
      'users': 'Users',
      'user-management': 'User Management',
      'access-control': 'Access Control',
      'api-keys': 'API Keys',
      'branch-configuration': 'Branch Configuration',
      'settings': 'Settings',
      'intrusion-accounts': 'Intrusion Accounts',
      'alerts': 'Alert Setting',
      'camera-streams': 'Camera & Configuration',
      'present': 'Present',
      'activity-tracking': 'Activity',
      'activity': 'Activity',
      'in-out': 'In/Out',
      'unmatched-groups': 'Unmatched Groups',
      'connect-kivo': 'Connect with Kivo',
      'live-monitoring': 'Live Monitoring',
      'banner-settings': 'Banner Setting',
      'notification-settings': 'Notification Settings',
      'activity-settings': 'Activity Settings',
      'ai-review': 'AI Matching',
      'unavailable': 'Unavailable',
      'intrusion': 'Intrusion',
      'export-to-kivo': 'Export to Kivo',
      'system-health-check': 'System Health Check',
      'detection-settings': 'Detection Setting',
      'face-embedding-config': 'Face Embedding Config',
      'late-entry-config': 'Late Entry Config',
      'recordings': 'Recordings',
      'speaker-configuration': 'Speaker configuration',
      'hooter-configuration': 'Hooter Configuration',
      'debug': 'Debug',
      'db-explorer': 'DB Explorer',
      'test': 'Test'
    };
    
    // Segments to skip in breadcrumb display (parent paths that shouldn't be shown)
    const skipSegments = ['audit', 'settings'];
    
    // Dynamic route handling - skip ID segments
    const isDynamicId = (segment) => {
      const value = String(segment || '').trim();
      if (!value) return false;
      // Never treat known route slugs as IDs.
      if (breadcrumbMap[value]) return false;
      // Skip if segment looks like an ID (numeric, UUID, or common opaque identifiers)
      if (/^\d+$/.test(value)) return true;
      if (/^[a-f0-9-]{36}$/i.test(value) && value.includes('-')) return true; // UUID
      if (/^[a-f0-9]{24}$/i.test(value)) return true; // Mongo-like ObjectId
      if (/^[a-f0-9]{32}$/i.test(value)) return true; // 32-char hex token
      // Heuristic for long opaque tokens (avoid skipping normal slugs like "employees").
      if (value.length >= 18 && /[a-zA-Z]/.test(value) && /\d/.test(value)) return true;
      return false;
    };

    const items = [
      {
        label: 'Dashboard',
        path: '/dashboard',
        icon: <HomeIcon sx={{ fontSize: 16 }} />,
      }
    ];

    let currentPath = '';
    let previousPath = '';
    pathSegments.forEach((segment, index) => {
      previousPath = currentPath;
      currentPath += `/${segment}`;
      
      // Skip segments that shouldn't be displayed in breadcrumb
      if (skipSegments.includes(segment)) {
        return;
      }
      
      // Skip dynamic ID segments
      if (isDynamicId(segment)) {
        return;
      }
      
      const label = breadcrumbMap[segment];
      
      // Always show mapped segments (except dashboard which is already added)
      if (label && segment !== 'dashboard') {
        items.push({
          label,
          path: currentPath,
        });
      }
      // For unmapped segments that are part of a known path structure, show them
      // This ensures 'intrusion-profile' shows before 'register-intrusion-profile'
      else if (!label && (segment === 'intrusion-profile' || segment === 'intrusion-profiles')) {
        items.push({
          label: 'Intrusion Profile',
          path: currentPath,
        });
      }
    });

    return items;
  };

  const handleNavigation = (path) => {
    navigate(path);
  };

  const breadcrumbItems = getBreadcrumbItems();

  return (
    <Box sx={{ mb: 2, ...sx }}>
      <Breadcrumbs aria-label="breadcrumb">
        {breadcrumbItems.map((item, index) => {
          const isLast = index === breadcrumbItems.length - 1;
          
          if (isLast) {
            return (
              <Typography
                key={item.path}
                color="text.primary"
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 0.5,
                  fontWeight: 'bold'
                }}
              >
                {item.icon}
                {item.label}
              </Typography>
            );
          }
          
          return (
            <Link
              key={item.path}
              underline="hover"
              color="inherit"
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handleNavigation(item.path);
              }}
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 0.5,
                cursor: 'pointer',
                '&:hover': {
                  color: 'primary.main',
                }
              }}
            >
              {item.icon}
              {item.label}
            </Link>
          );
        })}
      </Breadcrumbs>
    </Box>
  );
};

export default Breadcrumb;
